import { useState, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@shared/ui';
import { ProfileSidebar, ProfileInfo, PasswordSection, PaymentSection, SSOSection, NotificationSection } from '..';

export function ProfileModal({ open, onClose, dataTestId }) {
  const [activeTab, setActiveTab] = useState('profile');

  // Every opening starts on Organization Profile. This component stays mounted while the drawer is
  // closed, so without this it reopened on whichever tab was last used. Reset during render (on the
  // closed → open transition) rather than in an effect, so the old tab never paints — or mounts and
  // starts its own fetches — for a frame first.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setActiveTab('profile');
  }
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Updating profile...');

  // Stable reference: ProfileInfo re-runs its loading effect whenever this prop identity
  // changes, so an inline function here would immediately reset a loader set by a child
  // (e.g. the MFA toggle) on the next render. useCallback keeps it constant.
  const handleLoadingChange = useCallback((loading, message = 'Updating profile...') => {
    setIsLoading(loading);
    setLoadingMessage(message);
  }, []);
  const handleClose = () => {
    if (!isLoading && onClose) {
      onClose(false);
    }
  };

  // Organization Profile and My Profile are two halves of one form — see ProfileInfo.
  const handleProfileViewChange = useCallback((view) => {
    setActiveTab(view === 'personal' ? 'my-profile' : 'profile');
  }, []);

  const renderActiveSection = () => {
    switch (activeTab) {
      // Both tabs return ProfileInfo at the same position, so switching between them keeps the one
      // mounted instance (and its unsaved edits) and only changes which half is visible.
      case 'profile':
      case 'my-profile':
        return (
          <ProfileInfo
            onLoadingChange={handleLoadingChange}
            view={activeTab === 'my-profile' ? 'personal' : 'organization'}
            onViewChange={handleProfileViewChange}
          />
        );
      case 'password':
        return <PasswordSection onLoadingChange={handleLoadingChange} />;
      case 'payment':
        return <PaymentSection />;
      case 'sso':
        return <SSOSection onLoadingChange={handleLoadingChange} />;
      case 'notifications':
        return <NotificationSection onLoadingChange={handleLoadingChange} />;
      default:
        // An unknown tab id falls back to Organization Profile — with the same props as the real
        // tab, so a validation error on the other half can still switch to it.
        return (
          <ProfileInfo
            onLoadingChange={handleLoadingChange}
            view="organization"
            onViewChange={handleProfileViewChange}
          />
        );
    }
  };

  return (
    // onOpenChange is what the shared close button (and Escape) route through. handleClose
    // already refuses to close mid-save, so a save in flight is still protected.
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <SheetContent
        side="right"
        // 2xl (672px), as ManageSeats: room for the six tabs without the form running too wide.
        className="w-full sm:max-w-2xl overflow-hidden p-0"
        // Uses SheetContent's shared close button rather than its own, so every drawer matches.
        // The test id and loading-disabled state ride along via closeButtonProps.
        closeButtonProps={{
          'data-testid': dataTestId ? `${dataTestId}-close-button` : undefined,
          disabled: isLoading,
        }}
        data-testid={dataTestId}
      >
        <div className="h-full flex flex-col relative">
          {/* Close button comes from SheetContent; pr leaves room for it. */}
          <div className="flex-shrink-0 px-6 pt-6 pb-2 pr-16 flex justify-between items-start border-b border-line">
            <div className="flex-1">
              <SheetHeader className="p-0">
                <SheetTitle data-testid={dataTestId ? `${dataTestId}-title` : undefined}>Profile Settings</SheetTitle>
                {/* The shared drawer subtitle (as the Team, Invoice and Quote drawers), which also gives
                    the dialog its accessible description. */}
                <SheetDescription className="mt-1" data-testid={dataTestId ? `${dataTestId}-subtitle` : undefined}>
                  Manage your account settings and preferences
                </SheetDescription>
              </SheetHeader>
            </div>
          </div>

          {/* Tabs - disabled when loading */}
          <div className="flex-shrink-0 px-6 pt-6 pb-2">
            <ProfileSidebar activeTab={activeTab} onTabChange={setActiveTab} disabled={isLoading} dataTestId={dataTestId ? `${dataTestId}-sidebar` : undefined} />
          </div>

          {/* Content - with loader overlay */}
          <div className="flex-1 overflow-y-auto relative px-6 pt-6 pb-4" data-testid={dataTestId ? `${dataTestId}-content` : undefined}>
            {renderActiveSection()}
          </div>

          {/* Full modal loader overlay */}
          {isLoading && (
            // Veiled in the drawer's own panel colour (so it themes itself): with no background the
            // spinner and message sat straight on top of the form's text and read as part of it. 70%,
            // no blur: the form still shows through (80% + blur washed it out entirely) while the
            // spinner stays clear of the text behind it (50% let the two blend).
            <div className="absolute inset-0 flex items-center justify-center z-50 bg-surface-panel/70" data-testid={dataTestId ? `${dataTestId}-loading-overlay` : undefined}>
              <div className="flex flex-col items-center space-y-3">
                <div className="h-8 w-8 animate-spin border-4 border-brand border-t-transparent rounded-full" data-testid={dataTestId ? `${dataTestId}-loading-spinner` : undefined}></div>
                <p className="text-sm font-medium text-fg" data-testid={dataTestId ? `${dataTestId}-loading-message` : undefined}>{loadingMessage}</p>
              </div>
            </div>
          )}
         
        </div>
      </SheetContent>
    </Sheet>
  );
}
